import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

const CallLogForm = ({ currentUser }) => {
  // State for form data
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: new Date().toISOString().split('T')[1].substring(0, 5),
    endTime: '',
    callAttendTime: new Date().toISOString().split('T')[1].substring(0, 5),
    attendant: currentUser ? currentUser.name : '',
    assetId: '',
    serialNo: '',
    floor: '', // New floor field
    department: '',
    company: '', // New company field
    equipmentName: '',
    model: '', // New model field
    natureOfComplaint: '',
    actionTaken: '',
    sparesUsed: 'NA',
    sparesCost: 'NA',
    savingsAfterSparesCost: 'NA',
    type: 'BME', // BME, CMC, Rental, Warranty
    callStatus: 'Resolved (Complete)', // Pending, In Progress, Resolved (Temporary), Resolved (Complete)
    remarks: '',
    // New fields
    snN: '', // Serial Number
    responseTime: '',
    resolutionTime: '',
    attendedBy: 'INHOUSE', // INHOUSE / COMPANY ENGINEER
    callEndDate: new Date().toISOString().split('T')[0] // Set default to today's date
  });

  // State for manual entry mode
  const [manualEntryMode, setManualEntryMode] = useState(false);

  // State for assets (master database)
  const [assets, setAssets] = useState([]);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showPopup, setShowPopup] = useState(false);

  // State for suggestions
  const [suggestions, setSuggestions] = useState({
    assetId: [],
    serialNo: [],
    floor: [], // New floor suggestions
    department: [],
    company: [], // New company suggestions
    equipmentName: [],
    model: [] // New model suggestions
  });
  
  // State for suggestion visibility
  const [showSuggestions, setShowSuggestions] = useState({
    assetId: false,
    serialNo: false,
    floor: false, // New floor suggestion visibility
    department: false,
    company: false, // New company suggestion visibility
    equipmentName: false,
    model: false // New model suggestion visibility
  });

  // Refs for suggestion containers
  const assetIdRef = useRef(null);
  const serialNoRef = useRef(null);
  const floorRef = useRef(null); // New floor ref
  const departmentRef = useRef(null);
  const companyRef = useRef(null); // New company ref
  const equipmentNameRef = useRef(null);
  const modelRef = useRef(null); // New model ref

  // Load assets from localStorage (master database) on component mount
  useEffect(() => {
    loadAssets();
    
    // Set default start time and call attend time to current local time
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const currentTime = `${hours}:${minutes}`;
    
    setFormData(prev => ({
      ...prev,
      startTime: currentTime,
      callAttendTime: currentTime
    }));
    
    // Reset time tracking to ensure real-time updates work
    timeFieldsManuallyChanged.current = {
      startTime: false,
      callAttendTime: false
    };
    
    // Add click outside listener for suggestions
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Monitor company field changes
  useEffect(() => {
    console.log('ðŸ¢ Company field updated:', formData.company);
  }, [formData.company]);
  
  // Monitor all form data changes for debugging
  useEffect(() => {
    console.log('ðŸ“‹ Full form data updated:', formData);
  }, [formData]);

  // Track if time fields have been manually changed
  const timeFieldsManuallyChanged = useRef({
    startTime: false,
    callAttendTime: false
  });

  // Update time fields every 5 seconds to show accurate real-time values
  useEffect(() => {
    const interval = setInterval(() => {
      // Always update to current local time, but respect user changes
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const currentTime = `${hours}:${minutes}`;
      
      setFormData(prev => {
        // Only update fields that haven't been manually changed
        const newStartTime = timeFieldsManuallyChanged.current.startTime ? prev.startTime : currentTime;
        const newCallAttendTime = timeFieldsManuallyChanged.current.callAttendTime ? prev.callAttendTime : currentTime;
        
        return {
          ...prev,
          startTime: newStartTime,
          callAttendTime: newCallAttendTime
        };
      });
    }, 5000); // Update every 5 seconds for better accuracy

    return () => clearInterval(interval);
  }, []);

  // Handle click outside to close suggestions
  const handleClickOutside = (event) => {
    if (assetIdRef.current && !assetIdRef.current.contains(event.target)) {
      setShowSuggestions(prev => ({ ...prev, assetId: false }));
    }
    if (serialNoRef.current && !serialNoRef.current.contains(event.target)) {
      setShowSuggestions(prev => ({ ...prev, serialNo: false }));
    }
    if (floorRef.current && !floorRef.current.contains(event.target)) { // New floor handler
      setShowSuggestions(prev => ({ ...prev, floor: false }));
    }
    if (departmentRef.current && !departmentRef.current.contains(event.target)) {
      setShowSuggestions(prev => ({ ...prev, department: false }));
    }
    if (companyRef.current && !companyRef.current.contains(event.target)) { // New company handler
      setShowSuggestions(prev => ({ ...prev, company: false }));
    }
    if (equipmentNameRef.current && !equipmentNameRef.current.contains(event.target)) {
      setShowSuggestions(prev => ({ ...prev, equipmentName: false }));
    }
    if (modelRef.current && !modelRef.current.contains(event.target)) { // New model handler
      setShowSuggestions(prev => ({ ...prev, model: false }));
    }
  };

  const loadAssets = async () => {
    const normalizeAssets = (payload) => {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      return [];
    };

    try {
      console.log('Loading assets from API...');
      const response = await fetch('/api/assets');

      if (!response.ok) {
        throw new Error(`Failed to load assets (${response.status})`);
      }

      const payload = await response.json();
      const parsedAssets = normalizeAssets(payload);
      setAssets(parsedAssets);
    } catch (error) {
      console.error('Error loading assets:', error);
      setAssets([]);
      showMessage('Unable to load assets from server', 'error');
    }
  };
  // Create sample assets
  const createSampleAssets = async () => {
    const sampleAssets = [
      {
        id: uuidv4(),
        assetId: 'A001',
        serialNo: 'SN-789456',
        floor: 'Ground Floor',
        department: 'Radiology',
        company: 'MedTech Solutions',
        equipmentName: 'X-Ray Machine',
        model: 'XR-2000'
      },
      {
        id: uuidv4(),
        assetId: 'A002',
        serialNo: 'SN-123456',
        floor: 'First Floor',
        department: 'Cardiology',
        company: 'CardioSystems Inc',
        equipmentName: 'ECG Machine',
        model: 'ECG-1000'
      },
      {
        id: uuidv4(),
        assetId: 'A003',
        serialNo: 'SN-789123',
        floor: 'Second Floor',
        department: 'Laboratory',
        company: 'LabTech Corp',
        equipmentName: 'Centrifuge',
        model: 'CF-500'
      },
      {
        id: uuidv4(),
        assetId: 'A004',
        serialNo: 'SN-456789',
        floor: 'ICU Floor',
        department: 'ICU',
        company: 'CriticalCare Ltd',
        equipmentName: 'Ventilator',
        model: 'VT-3000'
      },
      {
        id: uuidv4(),
        assetId: 'A005',
        serialNo: 'SN-987654',
        floor: 'Surgery Floor',
        department: 'Surgery',
        company: 'Surgical Innovations',
        equipmentName: 'Anesthesia Machine',
        model: 'AM-1500'
      }
    ];
    
    // Save to API
    try {
      const promises = sampleAssets.map(asset => 
        fetch('/api/assets', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(asset),
        })
      );
      
      await Promise.all(promises);
      console.log('ðŸ“¦ Created sample assets and saved to API:', sampleAssets);
      setAssets(sampleAssets);
    } catch (error) {
      console.error('Failed to save sample assets:', error);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  // Get unique values from assets
  const getUniqueValues = (key) => {
    console.log(`ðŸ” Getting unique values for key: ${key}`);
    console.log(`ðŸ“š All assets for unique values:`, assets);
    
    // Ensure assets is an array
    if (!Array.isArray(assets)) {
      console.log('âš ï¸ Assets is not an array, returning empty array');
      return [];
    }
    
    const values = assets
      .filter(asset => asset && typeof asset === 'object') // Ensure asset is a valid object
      .map(asset => {
        // Handle potential undefined values
        const value = asset[key];
        // Ensure value is a string and not empty
        if (value === undefined || value === null) return null;
        const stringValue = String(value).trim();
        return stringValue.length > 0 ? stringValue : null;
      })
      .filter(val => val !== null); // Ensure value is a non-empty string
    
    console.log(`ðŸ“‹ Values before unique filter:`, values);
    const uniqueValues = [...new Set(values)];
    console.log(`ðŸŽ¯ Unique values for ${key}:`, uniqueValues);
    return uniqueValues;
  };

  // Get suggestions based on input value
  const getSuggestions = (value, type) => {
    console.log(`ðŸ” Getting suggestions for '${value}' of type '${type}'`);
    
    // Ensure type is valid
    if (!type) {
      console.log('âš ï¸ No type provided for suggestions');
      return [];
    }
    
    const uniqueValues = getUniqueValues(type);
    console.log(`ðŸ“š Unique values for ${type}:`, uniqueValues);
    
    // If no value provided, return all unique values (for onFocus when field is empty)
    if (!value || value.length < 1) {
      console.log('ðŸ” No value or value too short, returning all unique values');
      return uniqueValues.slice(0, 15); // Still limit to 15 for performance
    }
    
    // Make sure value is a string
    const searchValue = typeof value === 'string' ? value : String(value);
    const searchValueLower = searchValue.toLowerCase();
    
    // Enhanced fuzzy matching algorithm
    const filteredSuggestions = uniqueValues
      .filter(item => {
        if (!item) return false;
        const itemLower = item.toLowerCase();
        
        // Multiple matching strategies for better results
        const exactMatch = itemLower === searchValueLower;
        const startsWith = itemLower.startsWith(searchValueLower);
        const contains = itemLower.includes(searchValueLower);
        
        // For partial matches, we can also check if each character in search term exists in item
        let fuzzyScore = 0;
        if (!exactMatch && !startsWith && !contains) {
          // Calculate fuzzy score based on character matching
          let searchIndex = 0;
          for (let i = 0; i < itemLower.length && searchIndex < searchValueLower.length; i++) {
            if (itemLower[i] === searchValueLower[searchIndex]) {
              fuzzyScore++;
              searchIndex++;
            }
          }
        }
        
        const result = exactMatch || startsWith || contains || (fuzzyScore >= Math.min(searchValueLower.length, 3));
        console.log(`  Testing '${item}' against '${searchValue}': exact=${exactMatch}, startsWith=${startsWith}, contains=${contains}, fuzzyScore=${fuzzyScore}, result=${result}`);
        return result;
      })
      .sort((a, b) => {
        const aLower = a.toLowerCase();
        const bLower = b.toLowerCase();
        
        // Prioritize exact matches
        if (aLower === searchValueLower) return -1;
        if (bLower === searchValueLower) return 1;
        
        // Prioritize starts with matches
        if (aLower.startsWith(searchValueLower) && !bLower.startsWith(searchValueLower)) return -1;
        if (!aLower.startsWith(searchValueLower) && bLower.startsWith(searchValueLower)) return 1;
        
        // Prioritize contains matches
        if (aLower.includes(searchValueLower) && !bLower.includes(searchValueLower)) return -1;
        if (!aLower.includes(searchValueLower) && bLower.includes(searchValueLower)) return 1;
        
        // Sort by length (shorter is better for partial matches)
        return a.length - b.length;
      })
      .slice(0, 20); // Increased to 20 suggestions for better visibility

    console.log(`ðŸŽ¯ Filtered suggestions for ${type}:`, filteredSuggestions);
    return filteredSuggestions;
  };

  // Enhanced auto-fill function to ensure proper field population
  const performAutoFill = (searchValue, searchType) => {
    console.log(`ðŸ” Performing auto-fill for ${searchType}:`, searchValue);
    
    // Validate inputs
    if (!searchValue || searchValue.length === 0) {
      console.log('ðŸ” No search value provided, clearing auto-filled fields');
      return null;
    }
    
    if (!searchType) {
      console.log('âš ï¸ No search type provided');
      return null;
    }
    
    // Ensure assets is an array
    if (!Array.isArray(assets)) {
      console.log('âš ï¸ Assets is not an array');
      return null;
    }
    
    // Find matching asset
    let matchingAsset = null;
    
    // Ensure searchValue is a string
    const searchString = typeof searchValue === 'string' ? searchValue : String(searchValue);
    
    const currentAssets = assets;
    
    if (searchType === 'assetId') {
      matchingAsset = currentAssets.find(asset => 
        asset && 
        typeof asset === 'object' && 
        asset.assetId && 
        typeof asset.assetId === 'string' &&
        asset.assetId.toLowerCase() === searchString.toLowerCase()
      );
    } else if (searchType === 'serialNo') {
      matchingAsset = currentAssets.find(asset => 
        asset && 
        typeof asset === 'object' && 
        asset.serialNo && 
        String(asset.serialNo).toLowerCase() === searchString.toLowerCase()
      );
    }
    
    console.log('ðŸ“š All assets:', assets.length);
    console.log('ðŸŽ¯ Matching asset found:', matchingAsset);
    
    if (matchingAsset) {
      // Handle different possible company field names
      const companyValue = matchingAsset.company || 
                          matchingAsset.manufacturer || 
                          matchingAsset['Manufacturer/Company'] || 
                          matchingAsset['manufacturer/company'] || 
                          '';
      
      // Handle model field
      const modelValue = matchingAsset.model || '';
      
      const autoFilledData = {
        assetId: matchingAsset.assetId || '',
        serialNo: matchingAsset.serialNo || '',
        department: matchingAsset.department || '',
        company: companyValue,
        equipmentName: matchingAsset.equipmentName || '',
        floor: matchingAsset.floor || '',
        model: modelValue
      };
      
      console.log('ðŸ’¾ Auto-filled data:', autoFilledData);
      return autoFilledData;
    }
    
    console.log('âŒ No matching asset found');
    return null;
  };

  // Handle input change with improved auto-fill
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    console.log(`Input changed - Name: ${name}, Value: ${value}, Manual Mode: ${manualEntryMode}`);
    
    // Special handling for spares fields
    if (name === 'sparesUsed') {
      // If user removes "NA" from sparesUsed, also remove "NA" from sparesCost
      if (formData.sparesUsed === 'NA' && value !== 'NA') {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          sparesCost: value === '' ? 'NA' : ''
        }));
        return;
      }
      // If user enters "NA" in sparesUsed and sparesCost is empty, set sparesCost to "NA"
      if (value === 'NA' && (formData.sparesCost === '' || formData.sparesCost === 'NA')) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          sparesCost: 'NA'
        }));
        return;
      }
    }
    
    // Handle savingsAfterSparesCost field
    if (name === 'savingsAfterSparesCost') {
      // If user removes "NA" from savingsAfterSparesCost, set to empty
      if (formData.savingsAfterSparesCost === 'NA' && value !== 'NA') {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        return;
      }
      // If user enters "NA" in savingsAfterSparesCost
      if (value === 'NA') {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        return;
      }
    }
    
    // Handle auto-fill and suggestions
    switch (name) {
      case 'assetId':
        console.log('ðŸ“ Handling assetId input:', value);
        // Always update the field value regardless of mode
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        
        // Only perform auto-fill and show suggestions in auto-fill mode
        if (!manualEntryMode) {
          try {
            if (value.length > 0) {
              // Perform auto-fill
              const autoFilledData = performAutoFill(value, 'assetId');
              
              if (autoFilledData) {
                setFormData(prev => ({
                  ...prev,
                  ...autoFilledData,
                  // Keep the original field value
                  [name]: value
                }));
              }
              
              // Show suggestions
              const suggestions = getSuggestions(value, 'assetId');
              console.log('ðŸ’¡ Suggestions for assetId:', suggestions);
              setSuggestions(prev => ({ ...prev, assetId: suggestions }));
              setShowSuggestions(prev => ({ ...prev, assetId: suggestions.length > 0 }));
            } else {
              // Clear auto-filled fields when assetId is cleared
              setFormData(prev => ({
                ...prev,
                assetId: '',
                serialNo: '',
                department: '',
                company: '',
                equipmentName: '',
                floor: '',
                model: ''
              }));
              // Hide suggestions when field is empty
              setSuggestions(prev => ({ ...prev, assetId: [] }));
              setShowSuggestions(prev => ({ ...prev, assetId: false }));
            }
          } catch (error) {
            console.error('Error handling assetId input:', error);
            // Even if there's an error, still update the field value
            setFormData(prev => ({
              ...prev,
              [name]: value
            }));
          }
        }
        break;
        
      case 'serialNo':
        console.log('ðŸ“ Handling serialNo input:', value);
        // Always update the field value regardless of mode
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        
        // Only perform auto-fill and show suggestions in auto-fill mode
        if (!manualEntryMode) {
          try {
            if (value.length > 0) {
              // Perform auto-fill
              const autoFilledData = performAutoFill(value, 'serialNo');
              
              if (autoFilledData) {
                setFormData(prev => ({
                  ...prev,
                  ...autoFilledData,
                  // Keep the original field value
                  [name]: value
                }));
              }
              
              // Show suggestions
              const suggestions = getSuggestions(value, 'serialNo');
              console.log('ðŸ’¡ Suggestions for serialNo:', suggestions);
              setSuggestions(prev => ({ ...prev, serialNo: suggestions }));
              setShowSuggestions(prev => ({ ...prev, serialNo: suggestions.length > 0 })); // Show only when there are suggestions
            } else {
              // Clear auto-filled fields when serialNo is cleared
              setFormData(prev => ({
                ...prev,
                assetId: '',
                serialNo: '',
                department: '',
                company: '',
                equipmentName: '',
                floor: '',
                model: ''
              }));
              // Hide suggestions when field is empty
              setSuggestions(prev => ({ ...prev, serialNo: [] }));
              setShowSuggestions(prev => ({ ...prev, serialNo: false }));
            }
          } catch (error) {
            console.error('Error handling serialNo input:', error);
            // Even if there's an error, still update the field value
            setFormData(prev => ({
              ...prev,
              [name]: value
            }));
          }
        }
        break;
        
      case 'floor':
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        if (value.length > 0) {
          const suggestions = getSuggestions(value, 'floor');
          setSuggestions(prev => ({ ...prev, floor: suggestions }));
          setShowSuggestions(prev => ({ ...prev, floor: true }));
        } else {
          setShowSuggestions(prev => ({ ...prev, floor: false }));
        }
        break;
        
      case 'department':
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        if (value.length > 0) {
          const suggestions = getSuggestions(value, 'department');
          setSuggestions(prev => ({ ...prev, department: suggestions }));
          setShowSuggestions(prev => ({ ...prev, department: true }));
        } else {
          setShowSuggestions(prev => ({ ...prev, department: false }));
        }
        break;
        
      case 'company':
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        if (value.length > 0) {
          const suggestions = getSuggestions(value, 'company');
          setSuggestions(prev => ({ ...prev, company: suggestions }));
          setShowSuggestions(prev => ({ ...prev, company: true }));
        } else {
          setShowSuggestions(prev => ({ ...prev, company: false }));
        }
        break;
        
      case 'equipmentName':
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        if (value.length > 0) {
          const suggestions = getSuggestions(value, 'equipmentName');
          setSuggestions(prev => ({ ...prev, equipmentName: suggestions }));
          setShowSuggestions(prev => ({ ...prev, equipmentName: true }));
        } else {
          setShowSuggestions(prev => ({ ...prev, equipmentName: false }));
        }
        break;
        
      case 'model':
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        if (value.length > 0) {
          const suggestions = getSuggestions(value, 'model');
          setSuggestions(prev => ({ ...prev, model: suggestions }));
          setShowSuggestions(prev => ({ ...prev, model: true }));
        } else {
          setShowSuggestions(prev => ({ ...prev, model: false }));
        }
        break;
        
      default:
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        break;
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (value, type) => {
    const selectedValue = String(value || '').trim();
    console.log('ðŸ‘† Suggestion clicked:', value, 'Type:', type);
    
    try {
      // If it's assetId or serialNo, auto-fill other fields
      if (type === 'assetId' || type === 'serialNo') {
        const currentAssets = assets;
        
        console.log('Searching in', currentAssets.length, 'assets');
        
        // Enhanced search to find matching asset
        const matchingAsset = currentAssets.find(asset => {
          if (type === 'assetId') {
            return asset && 
                   typeof asset === 'object' && 
                   asset.assetId && 
                   typeof asset.assetId === 'string' &&
                   asset.assetId.toLowerCase() === selectedValue.toLowerCase();
          } else if (type === 'serialNo') {
            return asset && 
                   typeof asset === 'object' && 
                   asset.serialNo && 
                   String(asset.serialNo).toLowerCase() === selectedValue.toLowerCase();
          }
          return false;
        });
        
        console.log('Matching asset found:', matchingAsset);
        
        setFormData(prev => {
          // Handle different possible company field names
          let companyValue = '';
          let modelValue = '';
          if (matchingAsset) {
            // Check for different possible company field names
            companyValue = matchingAsset.company || 
                          matchingAsset.manufacturer || 
                          matchingAsset['Manufacturer/Company'] || 
                          matchingAsset['manufacturer/company'] || 
                          '';
        
            // Check for model field
            modelValue = matchingAsset.model || '';
          }
      
        const newData = {
          ...prev,
          [type]: selectedValue,
          ...(matchingAsset ? {
            assetId: matchingAsset.assetId || '',
            serialNo: matchingAsset.serialNo || '',
            department: matchingAsset.department || '',
            company: companyValue,
            equipmentName: matchingAsset.equipmentName || '',
            floor: matchingAsset.floor || '',
            model: modelValue
          } : {})
        };
      
        console.log('ðŸ’¾ Setting form data from suggestion:', newData);
        console.log('ðŸ¢ Company value being set from suggestion:', companyValue);
        console.log('ðŸ“± Model value being set from suggestion:', modelValue);
      
        return newData;
      });
    } else {
      setFormData(prev => ({
        ...prev,
          [type]: selectedValue
      }));
    }
    } catch (error) {
      console.error('Error handling suggestion click:', error);
      // Still update the field value even if there's an error
      setFormData(prev => ({
        ...prev,
        [type]: value
      }));
    }
    
    // Hide suggestions for all types
    setShowSuggestions(prev => ({ ...prev, [type]: false }));
  };

  // Handle time field changes
  const handleTimeChange = (e) => {
    const { name, value } = e.target;
    
    // Mark field as manually changed
    if (name === 'startTime') {
      timeFieldsManuallyChanged.current.startTime = true;
    } else if (name === 'callAttendTime') {
      timeFieldsManuallyChanged.current.callAttendTime = true;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Calculate response time
  const calculateResponseTime = () => {
    if (!formData.date || !formData.startTime || !formData.callAttendTime) return '';
    
    try {
      const startDateTime = new Date(`${formData.date}T${formData.startTime}`);
      const attendDateTime = new Date(`${formData.date}T${formData.callAttendTime}`);
      
      // Handle case where call attend time is next day
      if (attendDateTime < startDateTime) {
        attendDateTime.setDate(attendDateTime.getDate() + 1);
      }
      
      const diffMs = attendDateTime - startDateTime;
      const diffMins = Math.floor(diffMs / 60000);
      
      // Convert to H:MM format
      const hours = Math.floor(diffMins / 60);
      const minutes = diffMins % 60;
      
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    } catch (error) {
      console.error('Error calculating response time:', error);
      return '';
    }
  };

  // Calculate resolution time
  const calculateResolutionTime = () => {
    if (!formData.date || !formData.startTime || !formData.endTime) return '';
    
    try {
      const startDateTime = new Date(`${formData.date}T${formData.startTime}`);
      const endDateTime = new Date(`${formData.date}T${formData.endTime}`);
      
      // Handle case where end time is next day
      if (endDateTime < startDateTime) {
        endDateTime.setDate(endDateTime.getDate() + 1);
      }
      
      const diffMs = endDateTime - startDateTime;
      const diffMins = Math.floor(diffMs / 60000);
      
      // Convert to H:MM format
      const hours = Math.floor(diffMins / 60);
      const minutes = diffMins % 60;
      
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    } catch (error) {
      console.error('Error calculating resolution time:', error);
      return '';
    }
  };

  // Toggle manual entry mode
  const toggleManualEntry = () => {
    setManualEntryMode(!manualEntryMode);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    // In manual mode, we still need some basic information
    if (manualEntryMode) {
      // In manual mode, check that at least some key fields are filled
      if (!formData.assetId && !formData.serialNo && !formData.floor && 
          !formData.department && !formData.company && !formData.equipmentName && !formData.model) {
        showMessage('Please fill in at least one asset information field in manual mode', 'error');
        return;
      }
    } else {
      // In auto-fill mode, all asset fields are required
      // Note: company field is populated from manufacturer field in assets data
      if (!formData.assetId || !formData.serialNo || !formData.floor || 
          !formData.department || !formData.company || !formData.equipmentName || !formData.model) {
        showMessage('Please fill in all required asset information fields', 'error');
        return;
      }
    }
    
    if (!formData.natureOfComplaint) {
      showMessage('Please enter the nature of complaint', 'error');
      return;
    }
    
    if (!formData.actionTaken) {
      showMessage('Please enter the action taken', 'error');
      return;
    }
    
    try {
      // Create new log entry with UUID
      const newLog = {
        id: uuidv4(),
        ...formData,
        responseTime: calculateResponseTime(),
        resolutionTime: calculateResolutionTime(),
        createdAt: new Date().toISOString(),
        submittedBy: currentUser?.username || '',
        submittedByRole: currentUser?.role || ''
      };
      
      // Save to API first so Call Service Details tab can immediately fetch it
      let savedToApi = false;
      try {
        const response = await fetch('/api/call-logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newLog),
        });

        if (response.ok) {
          savedToApi = true;
        }
      } catch (_apiError) {
        // fallback handled below
      }

      if (!savedToApi) {
        showMessage('Server unavailable. Data not saved locally; please retry when API is available.', 'error');
        return;
      }

        // Show success message
        showMessage('Service call logged successfully!', 'success');
        // Show popup notification
        setShowPopup(true);
        // Hide popup after 3 seconds
        setTimeout(() => setShowPopup(false), 3000);
        
        // Reset form (except date and attendant)
        setFormData(prev => ({
          ...prev,
          assetId: '',
          serialNo: '',
          floor: '',
          department: '',
          company: '',
          equipmentName: '',
          model: '',
          natureOfComplaint: '',
          actionTaken: '',
          sparesUsed: 'NA',
          sparesCost: 'NA',
          savingsAfterSparesCost: 'NA',
          endTime: '',
          callAttendTime: (() => {
            const now = new Date();
            const hours = now.getHours().toString().padStart(2, '0');
            const minutes = now.getMinutes().toString().padStart(2, '0');
            return `${hours}:${minutes}`;
          })(),
          responseTime: '',
          resolutionTime: '',
          callEndDate: new Date().toISOString().split('T')[0], // Reset to today's date
          callStatus: 'Resolved (Complete)',
          remarks: ''
        }));

        // Reset manual entry mode
        setManualEntryMode(false);
        
        // Reset time tracking
        timeFieldsManuallyChanged.current = {
          startTime: false,
          callAttendTime: false
        };
        
        // Reload assets to ensure we have the latest data
        loadAssets();
    } catch (error) {
      console.error('Failed to save service call:', error);
      showMessage('Failed to save service call. Please try again.', 'error');
    }
  };

  return (
    <div className="call-log-form-container">
      <div className="card">
        <h2 className="card-title">{"\u{1F4DD}"} Call Log Form</h2>
        
        {currentUser && (
          <div className="user-badge">            {"\u{1F464}"} Logged in as: {currentUser.name} ({currentUser.role})
          </div>
        )}
        
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? '\u2705' : '\u274C'} {message.text}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>{"\u{1F4C5}"} Call Information</h3>
            
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="date">{"\u{1F4C5}"} Call Date *</label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="startTime">{"\u23F0"} Call Time *</label>
                  <input
                    type="time"
                    id="startTime"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleTimeChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="callAttendTime">{"\u{1F4DE}"} Call Attend Time *</label>
                  <input
                    type="time"
                    id="callAttendTime"
                    name="callAttendTime"
                    value={formData.callAttendTime}
                    onChange={handleTimeChange}
                    required
                  />
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="endTime">{"\u{1F3C1}"} End Time *</label>
                  <input
                    type="time"
                    id="endTime"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleTimeChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="callEndDate">{"\u{1F4C5}"} Call End Date</label>
                  <input
                    type="date"
                    id="callEndDate"
                    name="callEndDate"
                    value={formData.callEndDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="responseTime">{"\u23F1\uFE0F"} Response Time</label>
                  <input
                    type="text"
                    id="responseTime"
                    name="responseTime"
                    value={calculateResponseTime()}
                    readOnly
                  />
                </div>
              </div>
              
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="resolutionTime">{"\u23F1\uFE0F"} Resolution Time</label>
                  <input
                    type="text"
                    id="resolutionTime"
                    name="resolutionTime"
                    value={calculateResolutionTime()}
                    readOnly
                  />
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="attendant">{"\u{1F464}"} Attendant *</label>
                  <input
                    type="text"
                    id="attendant"
                    name="attendant"
                    value={formData.attendant}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="attendedBy">{"\u{1F465}"} Attended By</label>
                  <select
                    id="attendedBy"
                    name="attendedBy"
                    value={formData.attendedBy}
                    onChange={handleInputChange}
                  >
                    <option value="INHOUSE">INHOUSE</option>
                    <option value="COMPANY ENGINEER">COMPANY ENGINEER</option>
                  </select>
                </div>
              </div>
              
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="type">{"\u{1F3F7}\uFE0F"} Type</label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                  >
                    <option value="BME">BME</option>
                    <option value="CMC">CMC</option>
                    <option value="Rental">Rental</option>
                    <option value="Warranty">Warranty</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h3>{"\u{1F3E2}"} Asset Information</h3>
            
            <div className="form-row">
              <div className="form-col" ref={assetIdRef}>
                <div className="form-group">
                  <label htmlFor="assetId">{"\u{1F194}"} Asset ID *</label>
                  <input
                    type="text"
                    id="assetId"
                    name="assetId"
                    value={formData.assetId}
                    onChange={handleInputChange}
                    required
                    // FIXED: Always allow editing in auto mode for searching
                    readOnly={false}
                    // Add debugging attributes
                    data-mode={manualEntryMode ? 'manual' : 'auto'}
                    data-read-only={false}
                    // Add specific styling to ensure it's visible as editable
                    style={{
                      backgroundColor: '#ffffff',
                      borderColor: '#4361ee',
                      color: '#000000'
                    }}
                    // Add onFocus handler - don't show suggestions when field is empty
                    onFocus={(e) => {
                      if (!manualEntryMode) {
                        // Don't show suggestions when field is empty on focus
                        setShowSuggestions(prev => ({ ...prev, assetId: false }));
                      }
                    }}
                  />
                  {/* Show suggestions when there are any - always show in auto mode */}
                  {(!manualEntryMode && showSuggestions.assetId && suggestions.assetId.length > 0) && (
                    <div className="suggestions-dropdown">
                      {suggestions.assetId.map((suggestion, index) => (
                        <div
                          key={index}
                          className="suggestion-item"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSuggestionClick(suggestion, 'assetId');
                          }}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col">
                <div className="manual-toggle">
                  <button 
                    className={`btn ${manualEntryMode ? 'btn-success' : 'btn-secondary'}`}
                    onClick={toggleManualEntry}
                  >
                    {manualEntryMode ? '\u{1F504} Switch to Auto-fill Mode' : '\u270D\uFE0F Switch to Manual Entry Mode'}
                  </button>
                  <div className={`mode-indicator ${manualEntryMode ? 'manual' : 'auto'}`}>
                    {manualEntryMode ? 'Manual Entry Mode: Enter equipment details manually' : 'Auto-fill Mode: Type Asset ID or Serial Number to auto-fill fields from master data'}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col" ref={serialNoRef}>
                <div className="form-group">
                  <label htmlFor="serialNo">{"\u{1F522}"} Serial Number *</label>
                  <input
                    type="text"
                    id="serialNo"
                    name="serialNo"
                    value={formData.serialNo}
                    onChange={handleInputChange}
                    required
                    // FIXED: Always allow editing in auto mode for searching
                    readOnly={false}
                    // Explicitly ensure the field is not disabled
                    disabled={false}
                    // Add debugging attributes
                    data-mode={manualEntryMode ? 'manual' : 'auto'}
                    data-read-only={false}
                    // Add specific styling to ensure it's visible as editable
                    style={{
                      backgroundColor: '#ffffff',
                      borderColor: '#4361ee',
                      color: '#000000'
                    }}
                    // Add onFocus handler - don't show suggestions when field is empty
                    onFocus={(e) => {
                      if (!manualEntryMode) {
                        // Don't show suggestions when field is empty on focus
                        setShowSuggestions(prev => ({ ...prev, serialNo: false }));
                      }
                    }}
                  />
                  {/* Show suggestions when there are any - always show in auto mode */}
                  {(!manualEntryMode && showSuggestions.serialNo && suggestions.serialNo && suggestions.serialNo.length > 0) && (
                    <div className="suggestions-dropdown">
                      {suggestions.serialNo.map((suggestion, index) => (
                        <div
                          key={index}
                          className="suggestion-item"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSuggestionClick(suggestion, 'serialNo');
                          }}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col" ref={floorRef}>
                <div className="form-group">
                  <label htmlFor="floor">{"\u{1F3E2}"} Floor *</label>
                  <input
                    type="text"
                    id="floor"
                    name="floor"
                    value={formData.floor}
                    onChange={handleInputChange}
                    required
                    readOnly={!manualEntryMode}
                  />
                  {showSuggestions.floor && suggestions.floor.length > 0 && !manualEntryMode && (
                    <div className="suggestions-dropdown">
                      {suggestions.floor.map((suggestion, index) => (
                        <div
                          key={index}
                          className="suggestion-item"
                          onClick={() => handleSuggestionClick(suggestion, 'floor')}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="form-col" ref={departmentRef}>
                <div className="form-group">
                  <label htmlFor="department">{"\u{1F3E5}"} Department *</label>
                  <input
                    type="text"
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    required
                    readOnly={!manualEntryMode}
                  />
                  {showSuggestions.department && suggestions.department.length > 0 && !manualEntryMode && (
                    <div className="suggestions-dropdown">
                      {suggestions.department.map((suggestion, index) => (
                        <div
                          key={index}
                          className="suggestion-item"
                          onClick={() => handleSuggestionClick(suggestion, 'department')}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col" ref={companyRef}>
                <div className="form-group">
                  <label htmlFor="company">{"\u{1F3E2}"} Manufacturer/Company *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    readOnly={!manualEntryMode}
                  />
                  {showSuggestions.company && suggestions.company.length > 0 && !manualEntryMode && (
                    <div className="suggestions-dropdown">
                      {suggestions.company.map((suggestion, index) => (
                        <div
                          key={index}
                          className="suggestion-item"
                          onClick={() => handleSuggestionClick(suggestion, 'company')}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="form-col" ref={equipmentNameRef}>
                <div className="form-group">
                  <label htmlFor="equipmentName">{"\u{1F527}"} Equipment Name *</label>
                  <input
                    type="text"
                    id="equipmentName"
                    name="equipmentName"
                    value={formData.equipmentName}
                    onChange={handleInputChange}
                    required
                    readOnly={!manualEntryMode}
                  />
                  {showSuggestions.equipmentName && suggestions.equipmentName.length > 0 && !manualEntryMode && (
                    <div className="suggestions-dropdown">
                      {suggestions.equipmentName.map((suggestion, index) => (
                        <div
                          key={index}
                          className="suggestion-item"
                          onClick={() => handleSuggestionClick(suggestion, 'equipmentName')}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col" ref={modelRef}>
                <div className="form-group">
                  <label htmlFor="model">{"\u{1F4F1}"} Model *</label>
                  <input
                    type="text"
                    id="model"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    required
                    readOnly={!manualEntryMode}
                  />
                  {showSuggestions.model && suggestions.model.length > 0 && !manualEntryMode && (
                    <div className="suggestions-dropdown">
                      {suggestions.model.map((suggestion, index) => (
                        <div
                          key={index}
                          className="suggestion-item"
                          onClick={() => handleSuggestionClick(suggestion, 'model')}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h3>{"\u{1F4CB}"} Service Details</h3>
            
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="natureOfComplaint">{"\u{1F50D}"} Nature of Complaint *</label>
                  <textarea
                    id="natureOfComplaint"
                    name="natureOfComplaint"
                    rows="3"
                    value={formData.natureOfComplaint}
                    onChange={handleInputChange}
                    placeholder="Describe the problem..."
                    required
                  ></textarea>
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="actionTaken">{"\u{1F6E0}\uFE0F"} Action Taken *</label>
                  <textarea
                    id="actionTaken"
                    name="actionTaken"
                    rows="3"
                    value={formData.actionTaken}
                    onChange={handleInputChange}
                    placeholder="Describe the actions taken to resolve the issue..."
                    required
                  ></textarea>
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="sparesUsed">{"\u2699\uFE0F"} Spares Used</label>
                  <input
                    type="text"
                    id="sparesUsed"
                    name="sparesUsed"
                    value={formData.sparesUsed}
                    onChange={handleInputChange}
                    placeholder="List any spares used..."
                  />
                </div>
              </div>
              
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="sparesCost">{"\u{1F4B0}"} Spares Cost</label>
                  <input
                    type="text"
                    id="sparesCost"
                    name="sparesCost"
                    value={formData.sparesCost}
                    onChange={handleInputChange}
                    placeholder="Enter cost of spares used..."
                  />
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="savingsAfterSparesCost">Savings</label>
                  <input
                    type="text"
                    id="savingsAfterSparesCost"
                    name="savingsAfterSparesCost"
                    value={formData.savingsAfterSparesCost}
                    onChange={handleInputChange}
                    placeholder="Enter savings after spares cost..."
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-col">
                <div className="form-group">
                  <label htmlFor="callStatus">{"\u{1F4CA}"} Call Status</label>
                  <select
                    id="callStatus"
                    name="callStatus"
                    value={formData.callStatus}
                    onChange={handleInputChange}
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Resolved (Temporary)">Resolved (Temporary)</option>
                    <option value="Resolved (Complete)">Resolved (Complete)</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="remarks">{"\u{1F4DD}"} Remarks</label>
              <textarea
                id="remarks"
                name="remarks"
                rows="2"
                value={formData.remarks}
                onChange={handleInputChange}
                placeholder="Any additional remarks..."
              ></textarea>
            </div>
          </div>
          
          <div className="form-actions">
            <button type="submit" className="btn btn-primary btn-block">              {"\u{1F4DD}"} Log Service Call
            </button>
            {showPopup && (
              <div className="popup-notification success">                {"\u2705"} Log done successfully!
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CallLogForm;

